import React, { useContext, useState } from "react";
import { TextField } from "@fluentui/react";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ViewportContext    from "../../../../context/studyviewer/ViewportContext";
import themeContext       from "../../../../context/theme/ThemeContext";
import toastContext       from "../../../../context/toast/ToastContext";
import userContext        from "../../../../context/users/UserContext";
import { TOAST_TYPE }     from "../../../../constants/Constants";
import ModalMain   from "../../../../components/Modal/ModalMain";
import ModalHeader from "../../../../components/Modal/ModalHeader";
import ModalFooter from "../../../../components/Modal/ModalFooter";
import { getViewport, getCurrentOuterIndex } from "../../helpers/CornerstoneViewportService";
import { collectPRSavePayload, savePRAnnotations, unselectedAIAnnotationUIDs } from "../../helpers/PRModalityService";
import "../../styles/styles.css";

/**
 * Floppy-disk button that opens a Save Presentation State dialog (same style
 * as ShareStudy / HistoryUpload) before posting the current viewport state to
 * the backend save-pr endpoint.
 */
const SaveAsPRButton = ({ viewportIndex }) => {
  const { studyParentId, waitForSeriesReadyAndMerge, setPrImages } = useContext(studyviewerContext);
  const { viewportSeriesData, spineLabels, annotationData, annotationDataAI } = useContext(ViewportContext);
  const { getThemeColor }  = useContext(themeContext);
  const { showToastv2 }    = useContext(toastContext);
  const { name, username } = useContext(userContext);

  // Creator Name is auto-filled from the logged-in user and shown read-only.
  const creatorDisplayName = name || username || "";

  const [saving,      setSaving]      = useState(false);
  const [status,      setStatus]      = useState(null); // null | "success" | "error"
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [description, setDescription] = useState("");
  const [creatorName, setCreatorName] = useState(creatorDisplayName);

  const openDialog = () => {
    setDescription("");
    setCreatorName(creatorDisplayName);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    closeDialog();
    if (saving) return;
    setSaving(true);
    setStatus(null);
    try {
      const vp = await getViewport(viewportIndex);
      if (!vp) throw new Error("Viewport not available");
      const imageId = vp.getCurrentImageId();
      if (!imageId) throw new Error("No image loaded in viewport");
      // Spine labels for THIS viewport (they live in React state, not the
      // Cornerstone annotation manager, so they must be passed in explicitly).
      const viewportSpineLabels = Array.isArray(spineLabels)
        ? spineLabels.filter((l) => l.viewportIndex === viewportIndex)
        : [];
      const payload = collectPRSavePayload(vp, imageId, studyParentId, {
        description,
        creatorName,
        spineLabels: viewportSpineLabels,
        // Detect viewer: AI-suggested boxes the user didn't select stay out of the PR.
        excludeAnnotationUIDs: unselectedAIAnnotationUIDs(annotationData, annotationDataAI),
      });
      const saveResponse = await savePRAnnotations(payload);
      setStatus("success");
      showToastv2(TOAST_TYPE.SUCCESS, "PR saved successfully");
      setTimeout(() => setStatus(null), 2500);

      // Flip the viewport's PR indicator for THIS image immediately. isPRPresent
      // is derived server-side across the whole study when fetching by series —
      // waitForSeriesReadyAndMerge only fetches the brand-new PR series' own
      // images (the PR document itself), never the source series this image
      // belongs to, so it never picks up the flag. Without this the PR icon
      // wouldn't appear until a full page refresh.
      //
      // Key this off currentStack.imageIds[currentIndex] rather than
      // vp.getCurrentImageId() — the star/badge checks (ViewportNavWidget,
      // Thumbnail) compare against that exact stack array, and cornerstone's
      // own runtime imageId isn't guaranteed to be the byte-identical string
      // (loader URL normalization, multi-frame swaps, etc.), so comparing
      // against anything else risks a silent mismatch.
      //
      // On a multi-frame clip the viewport index is a FRAME, not a series slot:
      // a mixed series reports its instance via getCurrentOuterIndex, and a
      // single-instance clip is always slot 0.
      const currentStack = viewportSeriesData?.[viewportIndex]?.stack;
      const outerIdx = getCurrentOuterIndex("viewport_" + viewportIndex);
      const currentIdx = outerIdx != null
        ? outerIdx
        : currentStack?.imageIds?.length === 1 ? 0 : vp.getCurrentImageIdIndex?.();
      const stackImageUrl = currentStack?.imageIds?.[currentIdx] ?? imageId;
      setPrImages?.((prev) => [...(Array.isArray(prev) ? prev : []), { imageUrl: stackImageUrl }]);

      // Wait for async DICOM ingestion to finish, then fetch + merge in ONLY the
      // new PR series — not awaited here so the button re-enables immediately.
      const processId = saveResponse?.data?.data?.processId;
      if (processId && typeof waitForSeriesReadyAndMerge === "function") {
        waitForSeriesReadyAndMerge(processId, payload.newSeriesInstanceUID).then((result) => {
          if (!result?.success) {
            console.warn("[SaveAsPRButton] Could not auto-merge new PR series:", result);
            showToastv2(TOAST_TYPE.INFO, "PR saved, but the viewer couldn't refresh automatically — reload to see it.");
          }
        });
      }
    } catch (err) {
      console.error("[SaveAsPRButton] Failed:", err);
      setStatus("error");
      showToastv2(TOAST_TYPE.ERROR, "Failed to save PR");
      setTimeout(() => setStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ── Fluent UI TextField styles for dark theme ───────────────────────────────
  const fieldStyles = {
    fieldGroup: { backgroundColor: "transparent", border: "1px solid #605e5c" },
    field: { color: getThemeColor("primary-color"), backgroundColor: "transparent" },
    label: { color: getThemeColor("primary-color"), fontWeight: 600, fontSize: 12 },
  };
  // Read-only Creator Name: greyed out so it visibly reads as non-editable — muted
  // grey text/label on a dimmed field, no text caret/cursor (a readOnly TextField is
  // still focusable and would show a blinking caret), and out of the tab order.
  const readOnlyFieldStyles = {
    ...fieldStyles,
    field: {
      ...fieldStyles.field,
      color: "#8a8886",              // muted grey text (disabled look)
      cursor: "default",
      caretColor: "transparent",
      userSelect: "none",
    },
    fieldGroup: {
      ...fieldStyles.fieldGroup,
      backgroundColor: "rgba(255,255,255,0.04)", // subtly dimmed field background
      border: "1px solid #3f3e3d",   // dimmer border than editable fields
      cursor: "default",
    },
    label: {
      ...fieldStyles.label,
      color: "#8a8886",              // greyed label too
    },
  };

  // ── Modal definition (ModalMain / ModalHeader / ModalFooter pattern) ─────────
  const modalDetails = {
    header: (
      <ModalHeader
        updatedModal={true}
        showPatientDetails={false}
        heading="Save Presentation State"
        handleCancel={closeDialog}
      />
    ),
    footer: (
      <ModalFooter
        DefaultButtonList={[{ title: "Cancel", onclick: closeDialog }]}
        PrimaryButtonList={[{ title: "Save",   onclick: handleSave  }]}
      />
    ),
    body: (
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
        <TextField
          label="Description"
          value={description}
          onChange={(_, v) => setDescription(v ?? "")}
          placeholder="e.g. Axial CT annotations"
          maxLength={64}
          styles={fieldStyles}
        />
        <TextField
          label="Creator Name"
          value={creatorName}
          readOnly
          tabIndex={-1}
          placeholder="e.g. Dr. Smith"
          maxLength={64}
          styles={readOnlyFieldStyles}
        />
      </div>
    ),
    width: "400px",
  };

  const title =
    saving               ? "Saving as PR…"
    : status === "success" ? "Saved as PR successfully"
    : status === "error"   ? "Failed to save as PR"
    :                        "Save viewport content as PR";

  return (
    <>
      {dialogOpen && (
        <ModalMain showModal={dialogOpen} modalDetails={modalDetails} />
      )}

      <div>
        <span
          className="pr-save-button"
          onClick={openDialog}
          title={title}
          style={{ opacity: saving ? 0.5 : 1, cursor: saving ? "wait" : "pointer" }}
        />
      </div>
    </>
  );
};

export default SaveAsPRButton;
